/**
 * #1001 — guarded WhatsApp Web tab reload.
 *
 * Observed 2026-10-30/31 (owner 69148fba): one WhatsApp Web tab stopped confirming anything and past episodes cleared
 * only on a page reload. The backend's account-media breaker can say "this engine is stuck"; this is the typed signal
 * it sends to THAT engine's client (`waGuardedReloadTool`, over the existing per-client MQTT request topic), and the
 * rule the extension applies before it touches the user's tab: never reload a tab somebody is using.
 */

/** Why the backend asked. Closed so a log line can never carry free text. */
export const GUARDED_RELOAD_REASONS = ['account_media_breaker'] as const;
export type GuardedReloadReason = (typeof GUARDED_RELOAD_REASONS)[number];

/** What the extension did with the request. `deferred` = the user is in the tab; the engine retries on its own. */
export const GUARDED_RELOAD_OUTCOMES = ['reloaded', 'deferred', 'skipped'] as const;
export type GuardedReloadOutcome = (typeof GUARDED_RELOAD_OUTCOMES)[number];

/** Why this outcome (the extension's decision log). */
export const GUARDED_RELOAD_DECISIONS = ['tab_not_focused', 'user_idle', 'user_active', 'rate_limited', 'no_tab', 'expired'] as const;
export type GuardedReloadDecision = (typeof GUARDED_RELOAD_DECISIONS)[number];

/** The user counts as idle after this long without input in the WhatsApp tab. */
export const GUARDED_RELOAD_IDLE_MS = 2 * 60_000;

export interface TabUseSnapshot {
    /** The WhatsApp tab is the active tab of its window. */
    tabActive: boolean;
    /** That window has OS focus. */
    windowFocused: boolean;
    /** Milliseconds since the last keyboard / pointer input in the tab; null when none was seen since the tab loaded. */
    msSinceInput: number | null;
}

/**
 * May the tab be reloaded right now? Yes when the user is not looking at it (not the active tab of a focused window)
 * or has not touched it for {@link GUARDED_RELOAD_IDLE_MS}. Otherwise the answer is "later".
 */
export function decideGuardedReload(snapshot: TabUseSnapshot, idleMs: number = GUARDED_RELOAD_IDLE_MS): { allow: boolean; decision: GuardedReloadDecision } {
    if (!snapshot.tabActive || !snapshot.windowFocused) return { allow: true, decision: 'tab_not_focused' };
    if (snapshot.msSinceInput === null || snapshot.msSinceInput >= idleMs) return { allow: true, decision: 'user_idle' };
    return { allow: false, decision: 'user_active' };
}
